import { useCallback, useEffect, useState } from 'react';
import { apiCall } from '../../utils/fetchAPI';
import { translateApiMessage } from '../../utils/apiMessages';

/**
 * A resident's home upkeep list.
 *
 * Finished one-off tasks are out of the way by default; the toggle brings them
 * back for the "did I already do that?" question.
 */
export function useTasks() {
  const [tasks, setTasks] = useState([]);
  const [includeDone, setIncludeDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);

    try {
      const response = await apiCall(
        `/me/tasks?limit=100${includeDone ? '&done=1' : ''}`,
        { method: 'GET' }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(translateApiMessage(data.error) || 'Votre liste n’a pas pu être chargée.');
      }

      setTasks(data.data || []);
      setError('');
    } catch (err) {
      setTasks([]);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [includeDone]);

  useEffect(() => {
    load();
  }, [load]);

  /** Returns an error message, or an empty string on success. */
  const save = useCallback(
    async (task, uuid = null) => {
      const response = await apiCall(uuid ? `/me/tasks/${uuid}` : '/me/tasks', {
        method: uuid ? 'PUT' : 'POST',
        body: JSON.stringify(task),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        return translateApiMessage(data.error) || 'La tâche n’a pas pu être enregistrée.';
      }

      await load();

      return '';
    },
    [load]
  );

  const setDone = useCallback(
    async (uuid, done) => {
      const response = await apiCall(`/me/tasks/${uuid}/done`, {
        method: 'PUT',
        body: JSON.stringify({ done }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));

        return translateApiMessage(data.error) || 'La tâche n’a pas pu être mise à jour.';
      }

      await load();

      return '';
    },
    [load]
  );

  const remove = useCallback(
    async (uuid) => {
      const response = await apiCall(`/me/tasks/${uuid}`, { method: 'DELETE' });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));

        return translateApiMessage(data.error) || 'La tâche n’a pas pu être supprimée.';
      }

      await load();

      return '';
    },
    [load]
  );

  return { tasks, loading, error, includeDone, setIncludeDone, reload: load, save, setDone, remove };
}
